import SearchDrawer from "v2/components/SearchDrawer/ui";
import { TradeCall } from "types";
import { Typography, useTheme } from "@mui/material";
import { AuthCheckBlocker } from "components/Blockers";
import { Search } from "@mui/icons-material";
import { useState } from "react";
import { useInViewInfiniteQuery } from "hooks/useInViewInfiniteQuery";
import { CHAIN_CHECK_LIST, CHAIN_CONFIG } from "chainConfigs";
import {
  Chain,
  CHAINS,
  NATIVE_CURRENCY_CHECK_LIST,
  NATIVE_TOKEN_ADDRESS_LIST,
  WRAPPED_CURRENCY_CHECK_LIST,
  WRAPPED_TOKEN_ADDRESS_LIST,
} from "chainConfigs";
import { toCapitalize } from "lib/utils";
import axiosService from "services/axios";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import Scrollable from "v2/components/base/Scrollable/ui";
import { MainPageLayout } from "v2/layouts/MainPageLayout";
import { DiscoverLayout } from "v2/layouts/DiscoverLayout";
import Select from "v2/components/base/Select/ui";
import BasicButton from "v2/components/Buttons/BasicButton";
import TradeRowItem from "v2/components/ScrollableItem/TradeRowItem/ui";
import { VIEW_POINT_CONFIG } from "hooks/useCheckViewPoint";
import { useCheckViewPoint } from "hooks/useCheckViewPoint";

const UNSUPPORTED_CHAIN = ["ton"];
const UNSUPPORTED_TOKEN_ADDRESS = [...NATIVE_TOKEN_ADDRESS_LIST, ...WRAPPED_TOKEN_ADDRESS_LIST];
const UNSUPPORTED_SYMBOL = [...NATIVE_CURRENCY_CHECK_LIST, ...WRAPPED_CURRENCY_CHECK_LIST];

const chainSelectConfig = [
  {
    label: "All Chains",
    value: "all",
  },
  ...Object.entries(CHAINS)
    .filter(([key]) => key !== "ton")
    .map(([key, value]) => ({
      label: key === "bsc" ? "BSC" : toCapitalize(CHAIN_CONFIG[key as Chain].name),
      value: key,
    })),
];

const timePeriodSelectConfig = [
  {
    label: "5 minutes",
    value: "5m",
  },
  {
    label: "1 hour",
    value: "1h",
  },
  {
    label: "1 day",
    value: "1d",
  },
  {
    label: "7 days",
    value: "7d",
  },
];

const searchFilterDrawerConfig = {
  key: "tradeTokenSearch",
  placeholder: "Search token (name or address)",
};

export const TradePageV2 = () => {
  const theme = useTheme();
  const isMobile = useCheckViewPoint(VIEW_POINT_CONFIG.MOBILE);

  return (
    <AuthCheckBlocker>
      <MainPageLayout
        Title={
          <Typography variant={isMobile ? "mobile-h3" : "mobile-h2"}>
            Trade Trending Tokens
          </Typography>
        }>
        <TrendingTokenSection />
      </MainPageLayout>
    </AuthCheckBlocker>
  );
};

const TrendingTokenSection = () => {
  const theme = useTheme();

  /* Search Drawer */
  const [searchFilter, setSearchFilter] = useState(
    localStorage.getItem(searchFilterDrawerConfig.key),
  );
  const [isShowSearchDrawer, setIsShowSearchDrawer] = useState(false);
  const {
    data: tradeTokenSearchData,
    isLoading: isTradeTokenSearchLoading,
    isSuccess: isTradeTokenSearchSuccess,
  } = useQuery<TradeCall[]>({
    queryKey: ["trade-target-token", searchFilter],
    queryFn: () => {
      return axiosService.getTradeToken({
        query: searchFilter || "",
      });
    },
  });

  /* Chain */
  const [selectedChain, setSelectedChain] = useState(chainSelectConfig[0].value);

  /* Time Period */
  const [selectedTimePeriod, setSelectedTimePeriod] = useState(
    timePeriodSelectConfig.at(-1)!.value,
  );

  /* Data */
  const [page, setPage] = useState(1);
  const queryResult = useInfiniteQuery({
    queryKey: ["all-trade-calls", selectedChain, selectedTimePeriod, { page }],
    queryFn: async ({ pageParam }) => {
      return axiosService.getTradeAllTrendingTokens({
        chain: selectedChain,
        sortBy: selectedTimePeriod,
        page: pageParam,
        limit: 10,
      });
    },
    getNextPageParam: (lastPage, pages) => {
      return lastPage.page + 1;
    },

    getPreviousPageParam: (firstPage, pages) => {
      return firstPage.page - 1;
    },
    initialPageParam: 1,
  });

  const { ref, hasAllFetched } = useInViewInfiniteQuery(queryResult);

  const { data, isLoading } = queryResult;

  const handleChainChange = (event: any) => {
    setSelectedChain(event.target.value as string);
  };

  const handleTimePeriodChange = (event: any) => {
    setSelectedTimePeriod(event.target.value as string);
  };

  const handleCloseSearchDrawer = () => {
    setIsShowSearchDrawer(false);
  };

  const handleOpenSearchDrawer = () => {
    setIsShowSearchDrawer(true);
  };

  const handleSearch = (search: string) => {
    setSearchFilter(search);
  };

  return (
    <>
      <DiscoverLayout
        Filters={
          <>
            <BasicButton
              variant="black"
              sx={{ width: "48px", height: "45px" }}
              onClick={handleOpenSearchDrawer}>
              <Search sx={{ width: "16px", height: "16px" }} />
            </BasicButton>
            <Select
              sx={{ width: "fit-content", height: "45px" }}
              defaultValue={chainSelectConfig[0].value}
              value={selectedChain}
              onChange={handleChainChange}
              options={chainSelectConfig}
            />
            <Select
              sx={{ width: "fit-content", height: "45px" }}
              defaultValue={timePeriodSelectConfig.at(-1)?.value}
              value={selectedTimePeriod}
              onChange={handleTimePeriodChange}
              options={timePeriodSelectConfig}
            />
          </>
        }
        Scrollable={
          <Scrollable
            isLoading={isLoading}
            isEmpty={data?.pages[0].totalResults === 0}
            hasAllFetched={hasAllFetched}
            loadMoreRef={ref}>
            {data?.pages
              .flatMap((page) => page.results)
              .filter(
                (token: any) =>
                  CHAIN_CHECK_LIST.includes(token.chain) &&
                  !UNSUPPORTED_CHAIN.includes(token.chain) &&
                  !UNSUPPORTED_TOKEN_ADDRESS.includes(token.tokenAddress) &&
                  !UNSUPPORTED_SYMBOL.includes(token.symbol),
              )
              .map((call, index) => {
                const formattedCall = {
                  ...call,
                  performance: parseFloat(call.performance),
                  marketCap: parseFloat(call.marketCap),
                  liquidity: parseFloat(call.liquidity),
                  price: parseFloat(call.price),
                };
                return <TradeRowItem key={`all-trade-${index}`} tradeCall={formattedCall} />;
              })}
          </Scrollable>
        }
      />
      <SearchDrawer
        isOpenned={isShowSearchDrawer}
        data={tradeTokenSearchData || []}
        isLoadedData={isTradeTokenSearchSuccess}
        isLoadingData={isTradeTokenSearchLoading}
        cachedKey={searchFilterDrawerConfig.key}
        placeholder={searchFilterDrawerConfig.placeholder}
        isEmpty={tradeTokenSearchData?.length === 0}
        hasAllFetched={hasAllFetched}
        loadMoreRef={ref}
        close={handleCloseSearchDrawer}
        onSearch={handleSearch}>
        {!tradeTokenSearchData ||
        !Array.isArray(tradeTokenSearchData) ||
        tradeTokenSearchData.length <= 0
          ? null
          : tradeTokenSearchData
              .filter(
                (token: any) =>
                  CHAIN_CHECK_LIST.includes(token.chain) &&
                  !UNSUPPORTED_CHAIN.includes(token.chain) &&
                  !UNSUPPORTED_TOKEN_ADDRESS.includes(token.tokenAddress) &&
                  !UNSUPPORTED_SYMBOL.includes(token.symbol),
              )
              .map((token: any, index: number) => {
                const formattedCall = {
                  ...token,
                  performance: parseFloat(token.performance),
                  marketCap: parseFloat(token.marketCap),
                  liquidity: parseFloat(token.liquidity),
                  price: parseFloat(token.price),
                };
                return <TradeRowItem key={`search-${index}`} tradeCall={formattedCall} />;
              })}
      </SearchDrawer>
    </>
  );
};
